import { Component, EventEmitter, Output } from '@angular/core';
import { AppGlobals } from '@app/app.global';
import { SharedService } from '@app/core';
import * as XLSX from 'xlsx';
import { CompanyService } from '../../../company-management/services/company.service';
import { PriceDiscoveryService, Supplier } from '../../services/price-discovery.service';

@Component({
    selector: 'app-add-supplier-rate-card-file-modal',
    templateUrl: './add-supplier-rate-card-file-modal.component.html',
    styleUrls: ['./add-supplier-rate-card-file-modal.component.scss']
})
export class AddSupplierRateCardFileModalComponent {
    @Output() supplierAdded = new EventEmitter<any>();

    isVisible: boolean = false;
    selectedSupplierId: string | null = null;
    suppliers: Supplier[] = [];
    entityId: string = '';
    rateContractFile: File | null = null;
    isDragging: boolean = false;
    isSubmitting: boolean = false;
    isLoadingSuppliers: boolean = false;
    mode: 'add' | 'update' = 'add';
    isNewSupplier: boolean = false;
    newSupplierName: string = '';
    gstNumber: string = '';
    supplierAlias: string = '';
    mobileNumber: string = '';
    email: string = '';
    mobileError: string = '';
    emailError: string = '';
    lastFetchedGst: string = '';
    isFetchingGst: boolean = false;
    manualSupplierName: string = '';
    hasExistingRateCard: boolean = false;
    currentSupplierName: string = '';
    isLoadingExistingRateCard: boolean = false;

    readonly SUPPORTED_FILE_TYPES = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    readonly MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB

    constructor(
        private sharedService: SharedService,
        private priceDiscoveryService: PriceDiscoveryService,
        private companyService: CompanyService,
        private appGlobals: AppGlobals
    ) { }

    show(entityId: string, mode: 'add' | 'update' = 'add', supplierId: string | null = null, supplierName: string = '', hasExistingRateCard: boolean = false, forceNewSupplier: boolean = false): void {
        this.resetForm();
        this.entityId = entityId;
        this.mode = mode;
        this.hasExistingRateCard = hasExistingRateCard;
        this.currentSupplierName = supplierName;
        this.isVisible = true;
        // Prevent body scroll when sidebar is open
        document.body.style.overflow = 'hidden';

        if (this.mode === 'add') {
            if ((supplierName && !hasExistingRateCard) || forceNewSupplier) {
                this.isNewSupplier = true;
                this.newSupplierName = supplierName;
                this.manualSupplierName = supplierName;
            } else {
                this.fetchSuppliers();
            }
        } else if (supplierId) {
            this.selectedSupplierId = supplierId;
        }
    }

    hide(): void {
        this.isVisible = false;
        // Restore body scroll
        document.body.style.overflow = '';
        this.resetForm();
    }

    resetForm(): void {
        this.selectedSupplierId = null;
        this.suppliers = [];
        this.rateContractFile = null;
        this.isDragging = false;
        this.isSubmitting = false;
        this.isNewSupplier = false;
        this.newSupplierName = '';
        this.gstNumber = '';
        this.supplierAlias = '';
        this.mobileNumber = '';
        this.email = '';
        this.mobileError = '';
        this.emailError = '';
        this.lastFetchedGst = '';
        this.isFetchingGst = false;
        this.manualSupplierName = '';
        this.hasExistingRateCard = false;
        this.currentSupplierName = '';
        this.isLoadingExistingRateCard = false;
    }

    fetchSuppliers(): void {
        if (!this.entityId) return;

        this.isLoadingSuppliers = true;
        this.priceDiscoveryService.getSuppliers(this.entityId).subscribe({
            next: (suppliers: any) => {
                this.isLoadingSuppliers = false;
                // The supplier-list API returns an array of objects which might have different structure
                // Adjusting based on common patterns or specific API response
                this.suppliers = suppliers || [];
            },
            error: (error: any) => {
                this.isLoadingSuppliers = false;
                console.error('Error fetching suppliers:', error);
            }
        });
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;
    }

    onFileDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;

        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            this.processFile(files[0]);
        }
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.processFile(input.files[0]);
        }
    }

    addTagFn = (name: string) => {
        return { name: name, id: 'NEW_SUPPLIER_' + name, isNew: true };
    };

    onSupplierChange(event: any): void {
        if (event && event.isNew) {
            this.isNewSupplier = true;
            this.newSupplierName = event.name;
            this.manualSupplierName = event.name;
            this.selectedSupplierId = null;
        } else {
            this.isNewSupplier = false;
            this.selectedSupplierId = event?.id;
        }
    }

    onManualNameChange(): void {
        this.manualSupplierName = this.newSupplierName;
    }

    cancelNewSupplier(): void {
        this.isNewSupplier = false;
        this.newSupplierName = '';
        this.selectedSupplierId = null;
        this.gstNumber = '';
        this.supplierAlias = '';
    }

    processFile(file: File): void {
        // Validate file type
        if (!this.SUPPORTED_FILE_TYPES.includes(file.type)) {
            this.sharedService.errorAlert('Invalid file type. Please upload an Excel file.');
            return;
        }

        // Validate file size
        if (file.size > this.MAX_FILE_SIZE) {
            this.sharedService.errorAlert('File size exceeds 15 MB limit.');
            return;
        }

        this.rateContractFile = file;
    }

    removeFile(): void {
        this.rateContractFile = null;
    }

    downloadSampleContract(): void {
        // Download sample rate contract XLS file
        const link = document.createElement('a');
        link.href = 'assets/file/PM_ Sample Rate Card Format.xlsx';
        link.download = 'PM_ Sample Rate Card Format.xlsx';
        link.click();
    }

    downloadExistingRateCard(): void {
        if (!this.selectedSupplierId || !this.entityId || this.isLoadingExistingRateCard) return;

        this.isLoadingExistingRateCard = true;
        const params = {
            supplierId: this.selectedSupplierId,
            sortBy: 'itemName',
            sortOrder: 'ASC',
            pageSize: 10000,
            page: 1
        };

        this.priceDiscoveryService.getSupplierRateCardItems(params, this.entityId).subscribe({
            next: (res: any) => {
                const items = res.items || res.data?.items || [];
                if (items.length === 0) {
                    this.sharedService.errorAlert('No items found to export');
                    this.isLoadingExistingRateCard = false;
                    return;
                }
                this.exportToExcel(items);
                this.isLoadingExistingRateCard = false;
            },
            error: (error: any) => {
                console.error('Error fetching items for export', error);
                this.sharedService.errorAlert(error);
                this.isLoadingExistingRateCard = false;
            }
        });
    }

    private exportToExcel(items: any[]): void {
        const exportData = items.map(item => ({
            'Item Name': item.itemName || item.item_name,
            'Item Code': item.itemCode || item.item_code || '',
            'Unit': item.unit,
            'Rate': item.rate,
            'Tax Applicable': item.isTaxApplicable || item.is_tax_applicable ? 'Yes' : 'No'
        }));

        const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Rate Card Items');

        const fileName = `${this.currentSupplierName}_Rate_Card_${new Date().toLocaleDateString('en-CA')}.xlsx`;
        XLSX.writeFile(wb, fileName);
    }

    gstDetail(): void {
        const gst = this.gstNumber?.trim().toUpperCase();
        this.gstNumber = gst;

        if (!gst || gst.length !== 15) {
            this.lastFetchedGst = '';
            return;
        }

        if (this.isFetchingGst || gst === this.lastFetchedGst) {
            return;
        }

        this.isFetchingGst = true;
        this.companyService.gstDetail({ gst: gst }).subscribe({
            next: (response) => {
                this.isFetchingGst = false;
                this.lastFetchedGst = gst;
                const name = response?.legal_name || response?.legalName;
                if (name) {
                    this.newSupplierName = name;
                } else if (this.manualSupplierName) {
                    this.newSupplierName = this.manualSupplierName;
                }
            },
            error: (error) => {
                this.isFetchingGst = false;
                this.lastFetchedGst = gst;
                if (this.manualSupplierName) {
                    this.newSupplierName = this.manualSupplierName;
                }
                console.error('Error fetching GST details:', error);
            }
        });
    }

    validateMobile(): boolean {
        if (!this.mobileNumber) {
            this.mobileError = 'Mobile number is required';
            return false;
        }
        const mobileRegex = /^[0-9]{10}$/;
        if (!mobileRegex.test(this.mobileNumber)) {
            this.mobileError = 'Mobile number must be exactly 10 digits';
            return false;
        }
        this.mobileError = '';
        return true;
    }

    validateEmail(): boolean {
        if (!this.email) {
            this.emailError = '';
            return true; // non-mandatory
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.email)) {
            this.emailError = 'Please enter a valid email address';
            return false;
        }
        this.emailError = '';
        return true;
    }

    onMobileKeyPress(event: KeyboardEvent): void {
        const charCode = event.which ? event.which : event.keyCode;
        // Allow only digits (0-9)
        if (charCode < 48 || charCode > 57) {
            event.preventDefault();
        }
    }

    submitSupplier(): void {
        // Validate supplier selection
        if (!this.isNewSupplier && !this.selectedSupplierId) {
            this.sharedService.errorAlert('Please select a supplier');
            return;
        }

        if (this.isNewSupplier) {
            if (!this.newSupplierName) {
                this.sharedService.errorAlert('Supplier Name is required');
                return;
            }

            // Validate mobile and email
            if (!this.validateMobile()) {
                this.sharedService.errorAlert(this.mobileError);
                return;
            }
            if (!this.validateEmail()) {
                this.sharedService.errorAlert(this.emailError);
                return;
            }
        }

        // Validate rate contract file
        if (!this.rateContractFile) {
            this.sharedService.errorAlert('Rate contract file is required');
            return;
        }

        this.isSubmitting = true;

        const formData = new FormData();
        if (this.selectedSupplierId) {
            formData.append('supplierId', this.selectedSupplierId);
        }

        if (this.isNewSupplier) {
            formData.append('supplierName', this.newSupplierName);
            formData.append('gstNo', this.gstNumber);
            formData.append('alias', this.supplierAlias);
            formData.append('mobile', this.mobileNumber);
            formData.append('email', this.email);
        }

        // Explicitly pass filename to ensure proper multipart/form-data encoding
        formData.append('file', this.rateContractFile, this.rateContractFile.name);

        const apiCall = this.mode === 'add'
            ? this.priceDiscoveryService.addSupplierWithRateCard(formData)
            : this.priceDiscoveryService.updateSupplierWithRateCard(formData);

        apiCall.subscribe({
            next: (response: any) => {
                this.isSubmitting = false;
                this.sharedService.successAlert(response.message);
                this.supplierAdded.emit(response);
                this.hide();
            },
            error: (error: any) => {
                this.isSubmitting = false;
                this.sharedService.errorAlert(error);
            }
        });
    }
}
